import org.w3c.dom.HTMLCanvasElement
import org.khronos.webgl.WebGLRenderingContext as GL
import vision.gears.webglmath.UniformProvider
import vision.gears.webglmath.Vec3
import kotlin.js.Date
import kotlin.math.*
import kotlin.random.Random

class Scene (
        val gl : WebGL2RenderingContext) : UniformProvider("scene") {

  val vsTrafo = Shader(gl, GL.VERTEX_SHADER, "shaders/trafo-vs.glsl")
  val fsTextured = Shader(gl, GL.FRAGMENT_SHADER, "shaders/textured-fs.glsl")
  val texturedProgram = Program(gl, vsTrafo, fsTextured)
  val camera = OrthoCamera(*Program.all)

  val quadGeometry = TexturedQuadGeometry(gl)

  val timeAtFirstFrame = Date().getTime()
  var timeAtLastFrame =  timeAtFirstFrame

  val avatarMaterial = Material(texturedProgram)
  val coronaVirusMaterial = Material(texturedProgram)

  val littleCoronaMaterial = Material(texturedProgram)
  val toiletPaperMaterial = Material(texturedProgram)
  val coronaBeerMaterial = Material(texturedProgram)

  val healthBarMaterial = Material(texturedProgram)
  val healthBarBackMaterial = Material(texturedProgram)
  val explosionMaterial = Material(texturedProgram)

  val titleMaterial = Material(texturedProgram)
  val instructionMaterial = Material(texturedProgram)
  val gameOverMaterial = Material(texturedProgram)
  val fatalityMaterial = Material(texturedProgram)

  val gameObjects = ArrayList<GameObject>()

  //A képernyő határai (átméretezés esetén változik)
  var leftborder = 1.0f
  var rightborder = 1.0f
  val upborder = 1.0f
  val downborder = -1.0f

  val coronaHealth = 150
  val avatarHealth = 100

  var moving = false
  var avatarDamaged = false
  var coronaDamaged = false
  var coronaDead = false

  var coronaHitPosition = Vec3()
  var avatarHitPosition = Vec3()

  //A gameover true-ra állítódik, ha vesztettünk, ha nyertünk, ez a játék végét jelzi
  var gameover = false
  var win = false
  //Legutóbbi lövés időpontja
  var shootTime = -1.5f

  //[AVATAR]
  //A mozgatható játékos
  val avatar = object : Character(Mesh(avatarMaterial, quadGeometry), Vec3(-1.65f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      if (!moving)
        roll = 0.0f
      if (health <= 0) {
        gameover = true
        return false
      }
      return true
    }
  }

  //Az ellenség: a nagy kék koronavírus...
  val coronaVirus = object : Character(Mesh(coronaVirusMaterial, quadGeometry), Vec3(1.65f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      roll += 0.005f
      if (health <= 0) {
        coronaDead = true
        gameover = true
        win = true
        return false
      }
      return true
    }
  }

  //A koronavírus zöld healthbarja
  val coronaHealthBar  = object : GameObject(Mesh(healthBarMaterial, quadGeometry), Vec3(coronaVirus.position) - Vec3(0.4f, 0.6f), 0.0f, Vec3(1.0f, 0.6f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      if (coronaVirus.health <= 0)
        return false
      return true
    }
  }

  //A koronavírus healthbarja mögötti piros csík (viszonyításnak)
  val coronaHealthBarBack  = object : GameObject(Mesh(healthBarBackMaterial, quadGeometry), Vec3(coronaVirus.position) - Vec3(0.4f, 0.6f), 0.0f, Vec3(1.0f, 0.6f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      if (avatar.health <= 0)
        return false
      return true
    }
  }

  //A játékos zöld healthbarja, amely mindig vele mozog
  val avatarHealthBar  = object : GameObject(Mesh(healthBarMaterial, quadGeometry), Vec3(avatar.position) - Vec3(0.4f, 0.4f), 0.0f, Vec3(1.0f, 0.6f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      position.set(Vec3(avatar.position) - Vec3(0.4f, 0.4f))
      if (avatar.health <= 0)
        return false
      return true
    }
  }

  //A játékos healthbarja mögötti piros csík (viszonyításnak), amely mindig vele mozog
  val avatarHealthBarBack  = object : GameObject(Mesh(healthBarBackMaterial, quadGeometry), Vec3(avatar.position) - Vec3(0.4f, 0.4f), 0.0f, Vec3(1.0f, 0.6f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      position.set(Vec3(avatar.position) - Vec3(0.4f, 0.4f))
      if (avatar.health <= 0)
        return false
      return true
    }
  }

  //A "Corona Shooter" felirat a képernyő tetején
  val title = object : GameObject(Mesh(titleMaterial, quadGeometry), Vec3(0.0f, 0.8f, 0.0f), 0.0f){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      return true
    }
  }

  //Segítség, hogy a space billentyűvel tudunk lőni
  val instruction = object : GameObject(Mesh(instructionMaterial, quadGeometry), Vec3(-1.5f, -0.85f, 0.0f), 0.0f, Vec3(0.7f, 0.7f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      return true
    }
  }

  //Amennyiben vesztünk, ez íródik ki
  val gameOver = object : GameObject(Mesh(gameOverMaterial, quadGeometry), Vec3(0.0f, 0.1f, 0.0f), 0.0f, Vec3(2.0f, 2.0f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      return true
    }
  }

  //Amennyiben győzünk, ez íródik ki
  val fatality = object : GameObject(Mesh(fatalityMaterial, quadGeometry), Vec3(0.0f, 0.1f, 0.0f), 0.0f, Vec3(2.0f, 2.0f)){
    override fun move(dt : Float, t : Float,
                      keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {
      return true
    }
  }

  init{
    gl.enable(GL.BLEND)
    gl.blendFunc( GL.SRC_ALPHA, GL.ONE_MINUS_SRC_ALPHA)
    addComponentsAndGatherUniforms(*Program.all)
    //Textúrák beállítása a materialokra
    avatarMaterial["colorTexture"]?.set(Texture2D(gl, "media/smudge_the_cat.png"))
    coronaVirusMaterial["colorTexture"]?.set(Texture2D(gl, "media/covid19.png"))
    littleCoronaMaterial["colorTexture"]?.set(Texture2D(gl, "media/little_corona.png"))
    toiletPaperMaterial["colorTexture"]?.set(Texture2D(gl, "media/toiletpaper.png"))
    coronaBeerMaterial["colorTexture"]?.set(Texture2D(gl, "media/corona_beer.png"))
    healthBarMaterial["colorTexture"]?.set(Texture2D(gl, "media/healthbar.png"))
    healthBarBackMaterial["colorTexture"]?.set(Texture2D(gl, "media/healthbar_back.png"))
    explosionMaterial["colorTexture"]?.set(Texture2D(gl, "media/boom.png"))
    titleMaterial["colorTexture"]?.set(Texture2D(gl, "media/title.png"))
    instructionMaterial["colorTexture"]?.set(Texture2D(gl, "media/instruction.png"))
    gameOverMaterial["colorTexture"]?.set(Texture2D(gl, "media/gameover.png"))
    fatalityMaterial["colorTexture"]?.set(Texture2D(gl, "media/fatality.png"))

    coronaVirus.health = coronaHealth
    avatar.health = avatarHealth

    avatar.scale.set(Vec3(0.4f, 0.4f))
    coronaVirus.scale.set(Vec3(0.5f, 0.5f))

    gameObjects.add(avatar)
    gameObjects.add(coronaVirus)
    gameObjects.add(coronaHealthBarBack)
    gameObjects.add(coronaHealthBar)
    gameObjects.add(avatarHealthBarBack)
    gameObjects.add(avatarHealthBar)
    gameObjects.add(title)
    gameObjects.add(instruction)
  }

  fun resize(gl : WebGL2RenderingContext, canvas : HTMLCanvasElement) {
    gl.viewport(0, 0, canvas.width, canvas.height)
    //Kamera és a látható kép állítása a képernyő átméretezése szerint
    camera.setAspectRatio(canvas.width.toFloat() / canvas.height.toFloat())
    rightborder = canvas.width.toFloat() / canvas.height.toFloat()
    leftborder = -rightborder
  }

  @Suppress("UNUSED_PARAMETER")
  fun update(gl : WebGL2RenderingContext, keysPressed : Set<String>) {

    val dt = (Date().getTime().toFloat() - timeAtLastFrame.toFloat()) / 1000.0f
    val t  = (Date().getTime().toFloat() - timeAtFirstFrame.toFloat()) / 1000.0f

    // clear the screen
    gl.clearColor(0.1f, 0.07f, 0.07f, 1.0f)
    gl.clearDepth(1.0f)
    gl.clear(GL.COLOR_BUFFER_BIT or GL.DEPTH_BUFFER_BIT)
    gl.activeTexture(GL.TEXTURE0)

    //Amennyiben vége a játéknak, fokozatosan eltűnnek az addig látható tárgyak
    if (gameover && t % 0.1f < 0.0275f && !gameObjects.isEmpty()) {
      gameObjects.forEach {
        it.update()
        it.draw(camera)
      }
      gameObjects.removeAt(gameObjects.count() - 1)
    }
    //Alapesetben így történik a kirajzolás
    else {
      gameObjects.forEach {
        //Az objektumok különböző feltételek esetén false-sal térnek vissza, ebből tudja a program, hogy nincs rájuk további szükség
        if (!it.move(dt, t, keysPressed, gameObjects)) {
          gameObjects.remove(it)
        }
        it.update()
        it.draw(camera)
      }
    }

    //Annak függvényében, hogy nyertünk-e, vagy vesztettünk, kirajzolódik a végén egy szöveg
    if (gameover) {
      gameObjects.remove(title)
      gameObjects.remove(instruction)
      if (!win) {
        gameOver.update()
        gameOver.draw()
      }
      else {
        fatality.update()
        fatality.draw()
      }
    }

    //A karaktert a nyilakkal, vagy a W, A, S és D billentyűkkel mozgathatjuk fel, balra, le és jobbra

    //Amennyiben nem mozgunk balra vagy jobbra, a moving változó false-ra állítódik
    if (!keysPressed.contains("RIGHT") && !keysPressed.contains("LEFT") && !keysPressed.contains("D") && !keysPressed.contains("A"))
      moving = false

    //A moving szerepe itt jön elő: ha jobbra mozgunk, egy kicsit előredől a játékos
    if((keysPressed.contains("RIGHT") || keysPressed.contains("D")) && (avatar.position - Vec3(rightborder, avatar.position.y)).length() >= 1.5f) {
      moving = true
      avatar.position += Vec3(0.03f)
      avatar.roll = -0.1f
    }

    //Ha balra mozgunk, egy kicsit hátradől a játékos
    if((keysPressed.contains("LEFT") || keysPressed.contains("A")) && (avatar.position - Vec3(leftborder, avatar.position.y)).length() >= 0.1f) {
      moving = true
      avatar.position -= Vec3(0.03f)
      avatar.roll = 0.1f
    }

    //Mozgatás felfelé
    if((keysPressed.contains("UP") || keysPressed.contains("W")) && (avatar.position - Vec3(avatar.position.x, upborder)).length() >= 0.1f) {
      avatar.position += Vec3(0.0f, 0.02f)
    }

    //Mozgatás lefelé
    if((keysPressed.contains("DOWN") || keysPressed.contains("S")) && (avatar.position - Vec3(avatar.position.x, downborder)).length() >= 0.1f) {
      avatar.position -= Vec3(0.0f, 0.02f)
    }

    //[ROBBANÁS]
    //Amennyiben a játékos sérült, azaz eltalálta valami, robbanás animáció játszódik le (amely egy új objektum az ütközés helyén)
    if(avatarDamaged) {
      val geom = TexturedQuadGeometry(gl)
      geom.reset()
      gameObjects.add(object : GameObject(Mesh(explosionMaterial, geom),
              Vec3(avatarHitPosition) + (avatar.position - avatarHitPosition) * 0.25f,
              avatarHitPosition.x,
              Vec3(1.0f / 6.0f, 1.0f / 6.0f)) {
        override fun move(dt : Float, t : Float,
                          keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {

          if(!explode(geom))
            return false

          gl.uniform1i(gl.getUniformLocation(texturedProgram.glProgram, "material.colorTexture"), 0)
          return true
        }
      })
      avatarDamaged = false
    }

    //Ugyanez a koronavírusba való ütközésnél
    if(coronaDamaged) {
      val geom = TexturedQuadGeometry(gl)
      geom.reset()
      gameObjects.add(object : GameObject(Mesh(explosionMaterial, geom),
              Vec3(coronaHitPosition) + Vec3(0.1f),
              coronaHitPosition.x,
              Vec3(1.0f / 6.0f, 1.0f / 6.0f)) {
        override fun move(dt : Float, t : Float,
                          keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {

          if(!explode(geom))
            return false

          gl.uniform1i(gl.getUniformLocation(texturedProgram.glProgram, "material.colorTexture"), 0)
          return true
        }
      })
      coronaDamaged = false
    }

    //Amennyiben legyőzzük a koronavírust, egy nagy robbanással lesz vége
    if(coronaDead) {
      val geom = TexturedQuadGeometry(gl)
      geom.reset()
      gameObjects.add(object : GameObject(Mesh(explosionMaterial, geom),
              Vec3(coronaVirus.position),
              0.0f,
              Vec3(0.5f, 0.5f)) {
        override fun move(dt : Float, t : Float,
                          keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {

          if(!explode(geom))
            return false

          gl.uniform1i(gl.getUniformLocation(texturedProgram.glProgram, "material.colorTexture"), 0)
          return true
        }
      })
      coronaDead = false
    }

    //[LÖVEDÉK]
    //A space billentyűvel fél másodpercenkénti lehűlési idővel wc-papírokat lőhetünk, hogy elpusztítsuk a koronavírust [hiszen ugyebár ezért fogy ennyire, nemde?]
    if(keysPressed.contains("SPACE") && !gameover && t - shootTime > 0.5f) {
        gameObjects.add(object : GameObject(Mesh(toiletPaperMaterial, quadGeometry),
                Vec3(avatar.position),
                0.0f,
                Vec3(0.15f, 0.15f)) {
          override fun move(dt: Float, t: Float,
                            keysPressed: Set<String>, gameObjects: List<GameObject>): Boolean {
            position += Vec3(0.1f)

            //[ÜTKÖZÉS]
            //Amennyiben elég közel kerül a lövedék a koronavírushoz, belerobban, és a koronavírus sérül 1-et
            if ((coronaVirus.position - position).length() < 0.5f) {
              coronaVirus.damage(1)
              coronaHealthBar.scale.set(Vec3(coronaVirus.health.toFloat() / coronaHealth.toFloat(), 0.6f))
              coronaHitPosition = this.position
              coronaDamaged = true
              return false
            }

            //Amennyiben a képernyőn túl helyezkedne el, nem érdemes tovább léteznie
            if ((position - Vec3(rightborder, position.y)).length() < 0.05f)
              return false

            return true
          }
        })
      //Legutóbbi lövés idejének beállítása
      shootTime = t
    }

    //[CÉLKÖVETŐ]
    //[TOLLAS]
    //Nagyobb időközönként a koronavírus "követőrakétákat", azaz Corona Extra söröket lövell a játékos felé.
    //Ezek a lejjebb látható sebességgel gyorsulnak.
    //Ezeket gyakorlatilag lehetetlen elkerülni, így gyakorlatilag egy figyelmeztetés a játékosnak, hogy nem elég
    //csak örökké kerülgetni a sima lövedékeket
    if(t % 17.5f > 15.0f && t % 0.5f < 0.0275f && !gameover)  {
      gameObjects.add(object : GameObject(Mesh(coronaBeerMaterial, quadGeometry),
              Vec3(coronaVirus.position),
              PI.toFloat() / 2.0f,
              Vec3(0.15f, 0.15f)) {
        override fun move(dt : Float, t : Float,
                          keysPressed : Set<String>, gameObjects : List<GameObject>):Boolean {

          //Találat esetén:
          if ((position - avatar.position).length() < 0.35f) {
            avatarHitPosition = this.position
            avatarDamaged = true
            avatar.damage(1)
            avatarHealthBar.scale.set(Vec3(avatar.health.toFloat() / avatarHealth.toFloat(), 0.6f))
            return false
          }

          //Irány beállítása a játékos pozíciójától függően
          var forwardVector = (avatar.position - this.position).normalize()
          position += (forwardVector / 200.0f) * velocity
          velocity *= 1.001f

          //Orientáció beállítása, a sörök mindig a játékos felé fognak nézni
          if (forwardVector.x < 0)
            roll = acos(forwardVector.y)
          else
            roll = -acos(forwardVector.y)
          return true
        }
      })
    }

    //Másodpercenként kisebb koronavírusokat lő felénk a nagy vírus, amelyeknek véletlenszerű
    //a kezdési szöge (iránya) és sebessége
    if ((t % 1) < 0.0275f && !gameover) {
      gameObjects.add(object : GameObject(Mesh(littleCoronaMaterial, quadGeometry),
              Vec3(coronaVirus.position),
              0.0f,
              Vec3(0.075f, 0.075f)) {
        //Irány és sebesség randomizáláhos szükséges változók
        val rand = Random.nextInt(1, 3)
        val xvelocity = Random.nextInt(1, 3).toFloat() / 100
        var yvelocity = Random.nextInt(1, 5).toFloat() / 100
        var xroll = (sqrt((xvelocity * xvelocity + yvelocity * yvelocity).toDouble())).toFloat()
        override fun move(dt: Float, t: Float,
                          keysPressed: Set<String>, gameObjects: List<GameObject>): Boolean {
          position -= when (rand) {
              1 -> Vec3(xvelocity, yvelocity)
              else -> Vec3(xvelocity, -yvelocity)
          }

          roll -= xroll

          if (((position - Vec3(rightborder, position.y)).length() < 0.05f)
                  || ((position - Vec3(leftborder, position.y)).length() < 0.05f))
            return false

          //[PATTANÓ?]
          //Amennyiben csak a felső, vagy az alsó határhoz ütköznek, nem tűnnek el, hanem visszapattannak
          if (((position - Vec3(position.x, upborder)).length() < 0.1f)
                  || ((position - Vec3(position.x, downborder)).length() < 0.1f)) {
            yvelocity *= -1
            xroll *= -1
          }

          //Játkosba való ütközés esetén szintén robbanó animációval eltűnik és sebez, csak most a játékoson
          if ((position - avatar.position).length() < 0.35f) {
            avatar.damage(1)
            avatarHealthBar.scale.set(Vec3(avatar.health.toFloat() / avatarHealth.toFloat(), 0.6f))
            avatarHitPosition = this.position
            avatarDamaged = true
            return false
          }

          return true
        }
      })
    }
  }

  //Robbanó animációhoz segédfüggvény
  fun explode(texGeo : TexturedQuadGeometry) : Boolean {

      if(!texGeo.addOffset(1.0f / 6.0f))
        return false

    return true
  }
}